import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LayoutGrid, ClipboardList, FileText, ShieldCheck, ScanLine, LogOut, Plus, Trash2, X, Download } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Seo } from "@/components/Seo";

const STAGES = ["Enquiry received", "Assessment pending", "Quote prepared", "Quote accepted", "Scheduled",
  "Technician assigned", "Installation in progress", "QC", "Completed", "Warranty activated"];

export default function MyErrah() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("surfaces");
  const [surfaces, setSurfaces] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [warranties, setWarranties] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [cert, setCert] = useState(null);

  const load = () => {
    api.get("/surfaces/mine").then(({ data }) => setSurfaces(data)).catch(() => {});
    api.get("/bookings/mine").then(({ data }) => setBookings(data)).catch(() => {});
    api.get("/quotes/mine").then(({ data }) => setQuotes(data)).catch(() => {});
    api.get("/warranties/mine").then(({ data }) => setWarranties(data)).catch(() => {});
  };
  useEffect(load, []);

  const downloadCert = async (w) => {
    try {
      const { data } = await api.get(`/warranties/${w.id}/certificate`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `ERRAH-Warranty-${w.certificate_no || "certificate"}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (_) { toast.error("Could not download certificate"); }
  };

  const tabs = [
    { key: "surfaces", label: "My Surfaces", icon: LayoutGrid },
    { key: "bookings", label: "Bookings", icon: ClipboardList },
    { key: "quotes", label: "Quotes", icon: FileText },
    { key: "warranty", label: "Warranty Vault", icon: ShieldCheck },
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="my-errah-page">
      <Seo title="My ERRAH" description="Manage your protected surfaces, bookings and warranties." />
      <header className="glass sticky top-0 z-30 px-5 lg:px-10 h-16 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate("/")} className="shrink-0"><img src="/errah-logo.png" alt="ERRAH" className="h-9 w-auto object-contain" /></button>
          <span className="font-display text-lg tracking-[0.2em] hidden sm:block">ERRAH</span>
          <span className="errah-label hidden sm:block">My ERRAH</span>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-400 hidden sm:block">{user?.name}</span>
          <button onClick={() => { logout(); navigate("/"); }} data-testid="logout-btn" className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors"><LogOut size={16} /> Logout</button>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-10">
        <h1 className="font-display text-4xl font-light tracking-tight">Welcome, {user?.name?.split(" ")[0]}.</h1>

        <div className="mt-8 flex flex-wrap gap-2 border-b border-white/10">
          {tabs.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}
              className={`inline-flex items-center gap-2 px-4 py-3 text-sm transition-colors border-b-2 -mb-px ${tab === t.key ? "border-[#C9A45C] text-white" : "border-transparent text-gray-500 hover:text-gray-300"}`}>
              <t.icon size={16} /> {t.label}
            </button>
          ))}
        </div>

        <div className="mt-8">
          {tab === "surfaces" && (
            <div data-testid="tab-content-surfaces">
              <div className="flex items-center justify-between mb-6">
                <p className="text-gray-400 text-sm">{surfaces.length} protected surface{surfaces.length !== 1 && "s"}</p>
                <button onClick={() => setShowAdd(true)} data-testid="add-surface-btn" className="inline-flex items-center gap-2 border border-white/20 px-4 py-2 text-sm hover:bg-white hover:text-black transition-colors"><Plus size={16} /> Add surface</button>
              </div>
              {surfaces.length === 0 ? <Empty text="No surfaces yet. Add your first protected surface." /> : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {surfaces.map((s) => (
                    <div key={s.id} className="hair-border bg-white/[0.02] p-6" data-testid={`surface-${s.id}`}>
                      <div className="flex items-start justify-between">
                        <div>
                          <span className="errah-label">{s.space}</span>
                          <h3 className="font-display text-lg mt-1">{s.label}</h3>
                        </div>
                        <button onClick={async () => { await api.delete(`/surfaces/${s.id}`); load(); }} className="text-gray-600 hover:text-red-400"><Trash2 size={15} /></button>
                      </div>
                      <p className="text-sm text-gray-400 mt-2">{s.material}</p>
                      {s.product && <p className="text-xs text-gray-500 mt-1">{s.product}</p>}
                      <span className="mt-4 inline-block px-3 py-1 text-xs text-[#10B981] border border-[#10B981]/40">{s.status}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === "bookings" && (
            <div data-testid="tab-content-bookings">
              {bookings.length === 0 ? <Empty text="No bookings yet." cta cb={() => navigate("/booking")} /> : (
                <div className="space-y-4">
                  {bookings.map((b) => (
                    <div key={b.id} className="hair-border bg-white/[0.02] p-6" data-testid={`booking-${b.id}`}>
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div><h3 className="font-display text-lg">{b.service}</h3><p className="text-sm text-gray-500">{b.surface_type} · {b.location || "—"} · {b.preferred_date || "TBD"}</p></div>
                        <span className="px-3 py-1 text-xs text-[#C9A45C] border border-[#C9A45C]/40">{b.status}</span>
                      </div>
                      <StatusTracker current={b.status} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === "quotes" && (
            <div data-testid="tab-content-quotes">
              {quotes.length === 0 ? <Empty text="No quote requests yet." cta ctaLabel="Request a quote" cb={() => navigate("/quote")} /> : (
                <div className="space-y-3">
                  {quotes.map((q) => (
                    <div key={q.id} className="hair-border bg-white/[0.02] p-5 flex flex-wrap items-center justify-between gap-2" data-testid={`quote-${q.id}`}>
                      <div><h3 className="font-display text-base">{q.surface_type || "Surface"} {q.preferred_service && `· ${q.preferred_service}`}</h3><p className="text-sm text-gray-500">{q.property_type} · {q.location || "—"}</p></div>
                      <div className="text-right">
                        <span className="px-3 py-1 text-xs border border-white/20 text-gray-300">{q.status}</span>
                        {q.estimate && <p className="text-sm gold-text mt-1">{q.estimate}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === "warranty" && (
            <div data-testid="tab-content-warranty">
              {warranties.length === 0 ? <Empty text="No warranties yet. They activate once an installation completes." /> : (
                <div className="grid md:grid-cols-2 gap-4">
                  {warranties.map((w) => (
                    <div key={w.id} className="hair-border bg-white/[0.02] p-6" data-testid={`warranty-${w.id}`}>
                      <div className="flex items-start justify-between">
                        <ShieldCheck className="text-[#C9A45C]" size={24} />
                        <span className={`px-3 py-1 text-xs border ${w.status === "Active" ? "text-[#10B981] border-[#10B981]/40" : "text-gray-400 border-white/20"}`}>{w.status}</span>
                      </div>
                      <h3 className="font-display text-xl mt-4">{w.product}</h3>
                      <p className="text-sm text-gray-400">{w.surface}</p>
                      <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                        <KV k="Installer" v={w.installer} />
                        <KV k="Install date" v={w.install_date} />
                        <KV k="Period" v={w.period} />
                        <KV k="Certificate" v={w.certificate_no} />
                        <KV k="Maintenance" v={w.maintenance_schedule} />
                      </div>
                      <div className="mt-5 flex gap-2">
                        <button onClick={() => setCert(w)} data-testid={`cert-${w.id}`} className="flex-1 border border-white/20 py-2.5 text-sm hover:bg-white hover:text-black transition-colors">View certificate</button>
                        <button onClick={() => downloadCert(w)} data-testid={`cert-pdf-${w.id}`} className="flex-1 inline-flex items-center justify-center gap-1.5 bg-[#C9A45C] text-black py-2.5 text-sm font-semibold hover:bg-[#E4C987] transition-colors"><Download size={14} /> PDF</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="mt-12 flex flex-wrap gap-3">
          <button onClick={() => navigate("/ai-scan")} className="inline-flex items-center gap-2 bg-[#C9A45C] text-black px-6 py-3 text-sm font-semibold hover:bg-[#E4C987] transition-colors"><ScanLine size={16} /> New AI Scan</button>
          <button onClick={() => navigate("/booking")} className="inline-flex items-center gap-2 border border-white/20 px-6 py-3 text-sm hover:bg-white hover:text-black transition-colors">Book installation</button>
        </div>
      </div>

      {showAdd && <AddSurface onClose={() => setShowAdd(false)} onDone={() => { setShowAdd(false); load(); }} />}
      {cert && <Certificate w={cert} user={user} onClose={() => setCert(null)} />}
    </div>
  );
}

function StatusTracker({ current }) {
  const idx = STAGES.indexOf(current);
  return (
    <div className="mt-5 overflow-x-auto">
      <div className="flex items-center gap-1 min-w-max">
        {STAGES.map((s, i) => (
          <div key={s} className="flex items-center gap-1">
            <div className="flex flex-col items-center">
              <div className={`w-3 h-3 rounded-full ${i <= idx ? "bg-[#C9A45C]" : "bg-white/15"}`} />
              <span className={`text-[9px] mt-1.5 whitespace-nowrap ${i <= idx ? "text-gray-300" : "text-gray-600"}`}>{s}</span>
            </div>
            {i < STAGES.length - 1 && <div className={`w-8 h-px ${i < idx ? "bg-[#C9A45C]" : "bg-white/15"}`} />}
          </div>
        ))}
      </div>
    </div>
  );
}

function KV({ k, v }) { return <div><p className="errah-label">{k}</p><p className="text-gray-300">{v}</p></div>; }
function Empty({ text, cta, cb, ctaLabel = "Book now" }) {
  return (
    <div className="hair-border bg-white/[0.02] p-16 text-center">
      <p className="text-gray-500">{text}</p>
      {cta && <button onClick={cb} className="mt-4 bg-[#C9A45C] text-black px-6 py-2.5 text-sm font-semibold hover:bg-[#E4C987] transition-colors">{ctaLabel}</button>}
    </div>
  );
}

function AddSurface({ onClose, onDone }) {
  const [f, setF] = useState({ space: "Home", label: "", material: "", product: "", status: "Protected" });
  const save = async () => {
    if (!f.label || !f.material) { toast.error("Label and material required"); return; }
    await api.post("/surfaces", f); onDone();
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center px-5" onClick={onClose}>
      <div className="glass p-8 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="add-surface-modal">
        <div className="flex items-center justify-between mb-6"><h3 className="font-display text-2xl">Add surface</h3><button onClick={onClose}><X size={20} /></button></div>
        <div className="space-y-3">
          <select value={f.space} onChange={(e) => setF({ ...f, space: e.target.value })} className="w-full bg-[#0A0A0A] border border-white/15 px-4 py-3 text-sm [color-scheme:dark]">
            {["Home", "Office", "Restaurant", "Hotel", "Retail", "Commercial", "Other"].map((o) => <option key={o}>{o}</option>)}
          </select>
          <input placeholder="Label (e.g. Living Room Table)" value={f.label} data-testid="surf-label" onChange={(e) => setF({ ...f, label: e.target.value })} className="w-full bg-transparent border border-white/15 px-4 py-3 text-sm focus:border-[#C9A45C]" />
          <input placeholder="Material (e.g. Italian Marble)" value={f.material} data-testid="surf-material" onChange={(e) => setF({ ...f, material: e.target.value })} className="w-full bg-transparent border border-white/15 px-4 py-3 text-sm focus:border-[#C9A45C]" />
          <input placeholder="Product (optional)" value={f.product} onChange={(e) => setF({ ...f, product: e.target.value })} className="w-full bg-transparent border border-white/15 px-4 py-3 text-sm focus:border-[#C9A45C]" />
        </div>
        <button onClick={save} data-testid="surf-save" className="mt-6 w-full bg-[#C9A45C] text-black py-3 text-sm font-semibold hover:bg-[#E4C987] transition-colors">Add surface</button>
      </div>
    </div>
  );
}

function Certificate({ w, user, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center px-5" onClick={onClose}>
      <div className="bg-[#0A0A0A] border border-[#C9A45C]/40 p-10 w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="certificate-modal">
        <div className="flex items-center justify-between"><span className="font-display text-2xl tracking-[0.25em]">ERRAH</span><button onClick={onClose}><X size={20} /></button></div>
        <div className="mt-8 border border-white/10 p-8 text-center">
          <ShieldCheck className="mx-auto text-[#C9A45C]" size={40} />
          <p className="errah-label mt-4">Digital Warranty Certificate</p>
          <h3 className="font-display text-2xl mt-2">{w.product}</h3>
          <p className="text-sm text-gray-400 mt-1">{w.surface}</p>
          <div className="mt-6 grid grid-cols-2 gap-4 text-left text-sm">
            <KV k="Certificate No" v={w.certificate_no} />
            <KV k="Holder" v={user?.name} />
            <KV k="Installer" v={w.installer} />
            <KV k="Install date" v={w.install_date} />
            <KV k="Period" v={w.period} />
            <KV k="Status" v={w.status} />
          </div>
          <p className="mt-8 text-xs text-gray-600">Issued by ERRAH · www.theerrah.com</p>
        </div>
      </div>
    </div>
  );
}
